import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, switchMap, tap, throwError, of, map, finalize } from "rxjs";
import { environment } from "src/environments/environment";
import { TokenService } from "src/app/core/services/token.service";



export interface PinnedNavItem {
  id?: number;
  userId: number;
  sideMenuId: number;
  note?: string;
  orderNo: number;
  creationUserId: number;
  nameEn?: string;
  nameAr?: string;
  isActive?: boolean;
}

@Injectable({ providedIn: "root" })
export class SideMenuService {
  private readonly baseURL = environment.apiUrl;
  private readonly SESSION_KEY = "sideMenu";
  private readonly PINNED_SESSION_KEY = "pinnedMenuItems";

  // Track pending pin operations to avoid conflicts
  private pendingPinOperations = new Set<number>(); // sideMenuIds being processed

  // Separate BehaviorSubjects for menu data and pinned items
  public menuData$ = new BehaviorSubject<any>(this.loadFromSession());
  public pinnedItems$ = new BehaviorSubject<PinnedNavItem[]>(this.loadPinnedFromSession());

  constructor(private http: HttpClient, public tokenService: TokenService) {
    // Load pinned items on service initialization
    this.loadPinnedItems();
  }

  private loadFromSession(): any {
    const sessionData = sessionStorage.getItem(this.SESSION_KEY);
    return sessionData ? JSON.parse(sessionData) : [];
  }

  private loadPinnedFromSession(): PinnedNavItem[] {
    const sessionData = sessionStorage.getItem(this.PINNED_SESSION_KEY);
    return sessionData ? JSON.parse(sessionData) : [];
  }

  private savePinnedToSession(items: PinnedNavItem[]): void {
    sessionStorage.setItem(this.PINNED_SESSION_KEY, JSON.stringify(items));
  }

  fetchMenu(userId: number): Observable<any> {
    return this.http
      .get<any>(`${this.baseURL}/side/menu/get-side-menu-data-by-id?id=${userId}`)
      .pipe(
        tap((menu) => {
          this.menuData$.next(menu);
          sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(menu));
        })
      );
  }

  getMenu(): Observable<any> {
    return this.menuData$.asObservable();
  }

  clearCache(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.PINNED_SESSION_KEY);
    this.menuData$.next([]);
    this.pinnedItems$.next([]);
    this.pendingPinOperations.clear();
  }

  // ---- Pinned items methods ----
  private loadPinnedItems(): void {
    const userId = this.tokenService.getUserIdFromToken();
    if (userId) {
      this.getPinnedItems().subscribe({
        next: (items) => {
        },
        error: (error) => {
        }
      });
    }
  }


  // In side-menu.service.ts, add this method to extract pinned items from menu data
  extractPinnedItemsFromMenu(): PinnedNavItem[] {
    const menuData = this.menuData$.value;
    if (!menuData || menuData.length === 0) return [];

    const pinnedItems: PinnedNavItem[] = [];

    const extractFromItems = (items: any[]) => {
      items.forEach(item => {
        if (item.displayOrder < 0) {
          pinnedItems.push({
            id: 0, // We don't have the DB ID from menu data
            userId: this.tokenService.getUserIdFromToken(),
            sideMenuId: item.id,
            orderNo: Math.abs(item.displayOrder),
            creationUserId: item.creationUserId || 0,
            nameEn: item.nameEn,
            nameAr: item.nameAr,
            isActive: true // We assume active since it's returned by the procedure
          });
        }
        if (item.children?.length) {
          extractFromItems(item.children);
        }
      });
    };

    extractFromItems(menuData);
    return pinnedItems;
  }

  getPinnedItems(): Observable<PinnedNavItem[]> {
    const userId = this.tokenService.getUserIdFromToken();

    // Get ALL pinned items (active and inactive) for this user
    return this.http.get<PinnedNavItem[]>(`${this.baseURL}/side/menu/user/get-all`).pipe(
      map(items => {
        // Filter items for current user (keep both active and inactive)
        return items.filter(item => item.userId === userId);
      }),
      tap((items) => {

        items.forEach(item => {
        });

        this.pinnedItems$.next(items);
        this.savePinnedToSession(items);
      }),
      catchError(error => {
        this.pinnedItems$.next([]);
        this.savePinnedToSession([]);
        return of([]);
      })
    );
  }

  getPinnedItemsObservable(): Observable<PinnedNavItem[]> {
    return this.pinnedItems$.asObservable();
  }

  getCurrentPinnedItems(): PinnedNavItem[] {
    return this.pinnedItems$.value;
  }

  // CORRECTED: Get next order number from the menu API response
  // In side-menu.service.ts
  private getNextOrderNumber(): number {
    const allPinned = this.pinnedItems$.value || [];
    if (allPinned.length === 0) return 1;

    const maxOrder = Math.max(...allPinned.map(item => item.orderNo || 0));
    return maxOrder + 1;
  }
  
  // In your SideMenuService - fix the pinItemById method to calculate correct order numbers

  pinItemById(sideMenuId: number, orderNo?: number): Observable<any> {
    const userId = this.tokenService.getUserIdFromToken();

    if (!userId || userId === 0) {
      return throwError(() => new Error('User not authenticated'));
    }

   

    // Get ALL current pinned items (active and inactive)
    const allPinnedItems = this.pinnedItems$.value || [];

    // Check if there's an existing inactive record for this item
    const existingInactiveItem = allPinnedItems.find(item =>
      item.sideMenuId === sideMenuId && item.isActive === false
    );

    if (existingInactiveItem) {
      return this.reactivatePinnedItem(existingInactiveItem.id!);
    }

    // Check for active pinned item
    const existingActiveItem = allPinnedItems.find(item =>
      item.sideMenuId === sideMenuId && item.isActive === true
    );

    if (existingActiveItem) {
      console.warn(`Item ${sideMenuId} is already pinned`);
      return throwError(() => new Error('Item is already pinned'));
    }

    const menuData = this.menuData$.value;
    if (!menuData || menuData.length === 0) {
      return this.fetchMenu(userId).pipe(
        switchMap(() => this.pinItemById(sideMenuId, orderNo))
      );
    }

    const menuItem = this.findMenuItemById(sideMenuId);
    

    // FIXED: Calculate INCREMENTAL order number using dedicated function
    let finalOrderNo: number = orderNo && orderNo > 0 ? orderNo : this.getNextOrderNumber();

    // Mark item as pending
    this.pendingPinOperations.add(sideMenuId);

    const now = new Date().toISOString();

    const payload = {
      id: 0,
      isActive: true,
      note: null,
      userDefined1: null,
      userDefined2: null,
      userDefined3: null,
      userDefined4: null,
      creationUserId: userId,
      creationDate: now,
      lastUpdateUserId: null,
      lastUpdateDate: null,
      deactivateUserId: null,
      deactivateDate: null,
      sideMenuId: menuItem.id,
      userId: userId,
      nameEn: menuItem.nameEn || "",
      nameAr: menuItem.nameAr || "",
      orderNo: finalOrderNo 
    };


    return this.http.post(`${this.baseURL}/side/menu/user/create`, payload).pipe(
      tap((response) => {
      }),
      catchError(error => {
        console.error(`Pin creation failed:`, error);
        return throwError(() => error);
      }),
      finalize(() => {
        this.pendingPinOperations.delete(sideMenuId);
        this.loadPinnedItems();
        this.fetchMenu(userId).subscribe();
      })
    );
  }


  private findMenuItemById(id: number): any {
    const menuData = this.menuData$.value;

    if (!menuData || menuData.length === 0) {
      return null;
    }

    const findInItems = (items: any[]): any => {
      if (!items || !Array.isArray(items)) return null;

      for (const item of items) {
        if (item.id === id) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = findInItems(item.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findInItems(menuData);
  }

  // Update unpinItemById to use sideMenuId
  unpinItemById(pinnedItemDbId: number): Observable<any> {
    if (!pinnedItemDbId || pinnedItemDbId === 0) {
      return throwError(() => new Error('Invalid pinned item ID'));
    }

    const userId = this.tokenService.getUserIdFromToken();

    // Find the item to get its sideMenuId
    const currentPinned = this.pinnedItems$.value || [];
    const pinnedItem = currentPinned.find(item => item.id === pinnedItemDbId);

    if (pinnedItem) {
      this.pendingPinOperations.add(pinnedItem.sideMenuId);
    }

    // IMPORTANT: Use PUT to delete endpoint that matches your API
    return this.http.put(`${this.baseURL}/side/menu/user/delete?id=${pinnedItemDbId}`, {}).pipe(
      tap((response) => {
      }),
      catchError(error => {
        console.error(`Unpin (delete) request failed:`, error);
        return throwError(() => error);
      }),
      finalize(() => {
        // Clear pending operation
        if (pinnedItem) {
          this.pendingPinOperations.delete(pinnedItem.sideMenuId);
        }

        // Reload data to get fresh state
        this.loadPinnedItems();
        this.fetchMenu(userId).subscribe();

      })
    );
  }


  // Also update the reactivatePinnedItem method
  reactivatePinnedItem(pinnedItemId: number): Observable<any> {
    if (!pinnedItemId || pinnedItemId === 0) {
      return throwError(() => new Error('Invalid pinned item ID'));
    }

    const userId = this.tokenService.getUserIdFromToken();

    // Send 'true' to reactivate the pinned item
    return this.http.put(`${this.baseURL}/side/menu/user/update-status/${pinnedItemId}`, true).pipe(
      tap((response) => {

        // Reload pinned items and menu data
        this.loadPinnedItems();
        this.fetchMenu(userId).subscribe();
      }),
      catchError(error => {
        console.error(`Reactivate (status update) request failed:`, error);
        return throwError(() => error);
      })
    );
  }

  isPinned(sideMenuId: number): boolean {
    // First check pending operations
    if (this.pendingPinOperations.has(sideMenuId)) {
      return true; // Consider it pinned while processing
    }

    // Check current ACTIVE pinned items from service
    const currentPinned = this.pinnedItems$.value || [];
    const isPinnedInService = currentPinned.some((item: PinnedNavItem) =>
      item.sideMenuId === sideMenuId && item.isActive !== false
    );

    // Also check menu data for userSideMenuIsActive status
    const menuItem = this.findMenuItemById(sideMenuId);
    const isPinnedInMenu = menuItem?.userSideMenuIsActive === true;

    return isPinnedInService || isPinnedInMenu;
  }

  // 5. Update getPinnedItem to only return active items
  getPinnedItem(sideMenuId: number): PinnedNavItem | undefined {
    const currentPinned = this.pinnedItems$.value;
    return currentPinned.find((item: PinnedNavItem) =>
      item.sideMenuId === sideMenuId && item.isActive !== false
    );
  }

  // Get pinned item details including DB ID
  getPinnedItemDetails(sideMenuId: number): Observable<PinnedNavItem> {
    // First check if we have it in our current data
    const currentPinned = this.pinnedItems$.value;
    const cachedItem = currentPinned.find(item => item.sideMenuId === sideMenuId);

    if (cachedItem) {
      return of(cachedItem);
    }

    // If not in cache, reload and find
    return this.getPinnedItems().pipe(
      map(items => {
        const item = items.find(i => i.sideMenuId === sideMenuId);
        if (!item) {
          throw new Error(`Pinned item not found for sideMenuId: ${sideMenuId}`);
        }
        return item;
      })
    );
  }

}